import classnames from 'classnames';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert } from '@zooplus/ui-components';

import { InitiatePaymentRequestData } from '../../adapters/retryApiAdapter.types';
import { useConfig } from '../../config/ConfigProvider';
import { initiatePayment } from '../../services/retryPaymentService';
import { getAdobeAccountEventTracking } from '../../services/trackingService';
import { RetryPaymentMethod } from '../../types';
import ActionCreator from '../Adyen/Authorization/ActionCreator';
import { useAppContext } from '../AppContextProvider';
import { OverlayIds, useOverlays } from '../OverlaysProvider';
import { useThunkDispatch } from '../providers/ThunkDispatchProvider';
import Svgs from '../svgs';

import styles from './LocalPaymentsRetryInitiator.module.scss';
import RetryNotificationBanner from './notification/RetryNotificationBanner';
import RetryPaymentNotification from './payment/RetryPaymentNotification';
import { useRetryState } from './RetryDataProvider';
import {
  getMobileAppsReturnUrl,
  getMobileAppType,
  getOrderDetailsUrl,
  isRecognizedApp,
} from './utils';

const trackRetryInitiateClick = (paymentMethod: RetryPaymentMethod, orderId: string) => {
  const adbAccEvent = getAdobeAccountEventTracking();
  adbAccEvent.track('retry_payment', {
    payment_method: paymentMethod,
    order_id: orderId,
  });
};

export interface LocalPaymentsRetryInitiatorProps
  extends Omit<InitiatePaymentRequestData, 'returnUrl' | 'hasAuthorizationRedirect'> {
  retryPaymentMethod: RetryPaymentMethod;
  isRetryPossible: boolean;
}

export const LocalPaymentsRetryInitiator: React.FC<LocalPaymentsRetryInitiatorProps> = ({
  orderId,
  customerId,
  retryPaymentMethod,
  isRetryPossible,
}) => {
  const [mobileAppType] = useState(() => {
    return getMobileAppType();
  });
  const { thunkDispatch } = useThunkDispatch();
  const {
    retryState: { initiatePaymentData, processAuthorisationData },
  } = useRetryState();
  const {
    config: {
      env,
      adyen: { clientKey },
    },
  } = useConfig();
  const { showOverlay } = useOverlays();
  const { t } = useTranslation();
  const {
    config: { appUrlSchema },
  } = useConfig();

  const { locale, shopBundle } = useAppContext();
  const isFetching = initiatePaymentData?.isFetching;
  const retryPaymentStatus = processAuthorisationData?.authorizationResult?.retryPaymentStatus;
  const isMobileApp = isRecognizedApp(mobileAppType);
  const hasAuthorizationRedirect =
    isMobileApp && (retryPaymentMethod === 'BANCONTACT_MOBILE' || retryPaymentMethod === 'TWINT');

  const returnUrl = isMobileApp
    ? getMobileAppsReturnUrl(appUrlSchema[mobileAppType], hasAuthorizationRedirect)
    : getOrderDetailsUrl();
  const isQrCodeAction = initiatePaymentData?.actionObject?.type === 'qrCode';
  const authorizationId = initiatePaymentData?.authorizationId;

  const handleInitiatePayment = () => {
    trackRetryInitiateClick(retryPaymentMethod, orderId);
    thunkDispatch(initiatePayment({ orderId, customerId, returnUrl, hasAuthorizationRedirect }));
  };

  useEffect(() => {
    if (hasAuthorizationRedirect && Boolean(authorizationId)) {
      window.location.assign(
        `${window.location.origin}/payment/apppaymentrouter?auth_id=${authorizationId}&termUrl=${returnUrl}`,
      );
    }
  }, [authorizationId, hasAuthorizationRedirect]);

  useEffect(() => {
    if (isQrCodeAction && !hasAuthorizationRedirect) {
      showOverlay(OverlayIds.BANCONTACT_MOBILE_AUTH);
    }
  }, [isQrCodeAction, showOverlay, hasAuthorizationRedirect]);

  const paymentMethodInterpolation =
    retryPaymentMethod === 'BANCONTACT_MOBILE' ? 'bancontact' : retryPaymentMethod.toLowerCase();

  const notificationBannerText = t('mzp.order.detail.payment.reminder.warning', {
    0: t('mzp.order.detail.retry.payment.button', {
      0: t(`general.payment.${paymentMethodInterpolation}`),
    }),
  });

  const ctaButtonText = t('mzp.order.detail.retry.payment.button', {
    0: t(`general.payment.${paymentMethodInterpolation}`),
  });

  const showRetryInitiator =
    isRetryPossible &&
    (retryPaymentStatus === undefined ||
      (retryPaymentStatus !== 'SUCCESSFUL' && retryPaymentStatus !== 'PROCESSING'));
  return (
    <>
      {retryPaymentStatus && (
        <RetryPaymentNotification
          retryPaymentStatus={retryPaymentStatus}
          retryPaymentMethod={retryPaymentMethod}
          orderId={orderId}
        />
      )}
      {initiatePaymentData?.isFailure && (
        <Alert className={styles.retryAlert} variant="warning">
          {t('mzp.order.detail.retry.payment.error.apicall')}
        </Alert>
      )}
      {initiatePaymentData?.actionObject && !isQrCodeAction && !hasAuthorizationRedirect && (
        <ActionCreator
          paymentAction={initiatePaymentData?.actionObject}
          clientKey={clientKey}
          adyenEnvironment={env === 'prod' ? 'live' : 'test'}
          locale={locale}
          paymentMethod="DP"
          shopBundle={shopBundle}
        />
      )}
      {showRetryInitiator && (
        <RetryNotificationBanner
          type="warning"
          id="retryPaymentNotification"
          ariaLabel={t('mzp.order.detail.retry.payment.accessibility.label')}
          text={notificationBannerText}>
          <button
            className={classnames(
              styles.retryPaymentButtonDarkWithPawLoader,
              styles.alwaysDisplayed,
              isFetching && styles.isLoading,
            )}
            disabled={isFetching}
            onClick={handleInitiatePayment}
            data-zta="retryPaymentButton">
            {!isFetching && <span>{ctaButtonText}</span>}
            {isFetching && <Svgs type="pawLoader" />}
          </button>
        </RetryNotificationBanner>
      )}
    </>
  );
};
